import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ref, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import './TimezoneClock.css';

const TimezoneClock = ({ userName }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [timezone, setTimezone] = useState('America/New_York'); // Default timezone

  // Listen for user's last login timezone
  useEffect(() => {
    const timezoneRef = ref(database, `users/${userName}/lastTimezone`);

    const unsubscribe = onValue(timezoneRef, (snapshot) => {
      if (snapshot.exists()) {
        setTimezone(snapshot.val());
      }
    });

    return () => unsubscribe();
  }, [userName]);

  // Update time every minute (to match old clock)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Get time in the user's timezone
  const getTimeInTimezone = () => {
    try {
      return new Date(currentTime.toLocaleString('en-US', { timeZone: timezone }));
    } catch (error) {
      console.error('Invalid timezone:', timezone);
      return currentTime;
    }
  };

  const timeInZone = getTimeInTimezone();
  const hours = timeInZone.getHours() % 12;
  const minutes = timeInZone.getMinutes();

  // Calculate angles (0 degrees = 12 o'clock)
  const hourAngle = (hours * 30 + minutes * 0.5 - 90) * (Math.PI / 180);
  const minuteAngle = (minutes * 6 - 90) * (Math.PI / 180);

  // Hour hand (shorter, thicker)
  const hourX = 100 + Math.cos(hourAngle) * 40;
  const hourY = 100 + Math.sin(hourAngle) * 40;

  // Minute hand (longer, thinner)
  const minuteX = 100 + Math.cos(minuteAngle) * 60;
  const minuteY = 100 + Math.sin(minuteAngle) * 60;

  return (
    <div className="timezone-clock">
      <motion.div
        className="timezone-clock-container"
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.3 }}
      >
        <svg
          className="timezone-clock-svg"
          viewBox="0 0 200 200"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Clock face */}
          <defs>
            <radialGradient id={`clockGradient-${userName}`}>
              <stop offset="0%" stopColor="#fef9f3" />
              <stop offset="100%" stopColor="#e8dfd5" />
            </radialGradient>
            <filter id={`clockShadow-${userName}`}>
              <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
              <feOffset dx="2" dy="2" result="offsetblur" />
              <feComponentTransfer>
                <feFuncA type="linear" slope="0.3" />
              </feComponentTransfer>
              <feMerge>
                <feMergeNode />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Clock outer rim */}
          <circle
            cx="100"
            cy="100"
            r="95"
            fill="#6b5644"
            filter={`url(#clockShadow-${userName})`}
          />

          {/* Clock face */}
          <circle
            cx="100"
            cy="100"
            r="85"
            fill={`url(#clockGradient-${userName})`}
            stroke="#a68a64"
            strokeWidth="2"
          />

          {/* Hour markers */}
          {[...Array(12)].map((_, i) => {
            const angle = (i * 30 - 90) * (Math.PI / 180);
            const x1 = 100 + Math.cos(angle) * 70;
            const y1 = 100 + Math.sin(angle) * 70;
            const x2 = 100 + Math.cos(angle) * 75;
            const y2 = 100 + Math.sin(angle) * 75;

            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#6b5644"
                strokeWidth="2"
                strokeLinecap="round"
              />
            );
          })}

          {/* Center decoration */}
          <circle cx="100" cy="100" r="8" fill="#6b5644" />
          <circle cx="100" cy="100" r="4" fill="#a68a64" />

          {/* Decorative heart at 12 o'clock */}
          <path
            d="M 100,35 Q 95,30 90,35 Q 85,40 90,45 L 100,52 L 110,45 Q 115,40 110,35 Q 105,30 100,35 Z"
            fill="#f2d4d7"
            stroke="#d9989f"
            strokeWidth="1"
          />

          {/* Clock hands */}
          {/* Hour hand (shorter, thicker) */}
          <line
            x1="100"
            y1="100"
            x2={hourX}
            y2={hourY}
            stroke="#6b5644"
            strokeWidth="4"
            strokeLinecap="round"
          />
          {/* Minute hand (longer, thinner) */}
          <line
            x1="100"
            y1="100"
            x2={minuteX}
            y2={minuteY}
            stroke="#a68a64"
            strokeWidth="3"
            strokeLinecap="round"
          />
          {/* Center dot on top */}
          <circle cx="100" cy="100" r="5" fill="#6b5644" />
        </svg>

        <motion.div
          className="timezone-clock-display"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <div className="timezone-label">{userName}</div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default TimezoneClock;
